import { useState, useMemo } from 'react';
import { useTheme } from '@mui/material/styles';
import { useTranslation } from 'react-i18next';
import Typography from '@mui/material/Typography';
import Tooltip from '@mui/material/Tooltip';
import Box from '@mui/material/Box';
import Stack from '@mui/material/Stack';
import { Info } from 'lucide-react';
import { TableComponent, type Column } from '@/components/TableComponent';
import DescriptionPopper from '@/components/DescriptionPopper';
import Card from '@/components/Card';
import { getInterferenceInfo } from '@/utils/scanner';
import {
  CongestionBarFill,
  HiddenSSIDLabel,
  SignalCellStack,
  EllipsisTypography,
  InterferenceDot,
  PopperHeaderStack,
  SignalBarWrapper,
  SignalDbmTypography,
  BssidCellTypography,
  ChannelCellTypography,
  BandwidthCellTypography,
  StandardCellTypography,
  TableColumnHeaderStack,
  InterferenceItemRow,
} from '../styles';

interface NetworksTableProps {
  networks: WifiNetwork[];
}

/** Displays a table of Wi-Fi networks with pagination and signal strength indicators. */
export function NetworksTable({ networks }: NetworksTableProps) {
  const [page, setPage] = useState(0);
  const { t } = useTranslation();
  const theme = useTheme();

  // Sort networks from least interference to highest interference
  const sortedNetworks = useMemo(() => {
    return [...networks].sort(
      (a, b) => (Number(a.signal) || -100) - (Number(b.signal) || -100)
    );
  }, [networks]);

  const columns: Column<WifiNetwork>[] = useMemo(
    () => [
      // Network SSID column
      {
        id: 'ssid',
        label: t('scanner.columns.ssid'),
        width: '26%',
        render: (_, row) => {
          const isHidden = !row.ssid;
          const displayTitle = row.ssid || t('scanner.hiddenNetwork');

          return (
            <Tooltip title={displayTitle} arrow placement="top-start">
              <EllipsisTypography
                variant="body2"
                fontWeight={500}
                color={theme.palette.white['06']}
                noWrap
                component="span"
              >
                {isHidden ? (
                  <HiddenSSIDLabel>
                    {t('scanner.hiddenNetwork')}
                  </HiddenSSIDLabel>
                ) : (
                  row.ssid
                )}
              </EllipsisTypography>
            </Tooltip>
          );
        },
      },
      // BSSID column
      {
        id: 'bssid',
        label: t('scanner.columns.bssid'),
        width: '16%',
        render: (val) => (
          <BssidCellTypography variant="body2">{val}</BssidCellTypography>
        ),
      },
      // Channel column
      {
        id: 'channel',
        label: t('scanner.columns.channel'),
        width: '7%',
        render: (val) => (
          <ChannelCellTypography variant="body2">{val}</ChannelCellTypography>
        ),
      },
      // Bandwidth column
      {
        id: 'bandwidth',
        label: t('scanner.columns.bandwidth'),
        width: '9%',
        render: (val) => (
          <BandwidthCellTypography variant="body2">
            {String(val || '')
              .toUpperCase()
              .endsWith('MHZ')
              ? val
              : `${val} MHz`}
          </BandwidthCellTypography>
        ),
      },
      // Signal Strength column (with Card Popper and explanation)
      {
        id: 'signal',
        label: (
          <TableColumnHeaderStack>
            <span>{t('scanner.columns.signal')}</span>
            <DescriptionPopper
              placement="top"
              offset={[-20, 14]}
              description={
                <Card maxWidth="380px" padding="16px">
                  <Typography
                    variant="body2"
                    fontWeight={700}
                    color={theme.palette.primary.main}
                    sx={{ mb: '8px' }}
                  >
                    {t('scanner.interference.scaleTitle')}
                  </Typography>
                  <Stack direction="column" spacing="8px">
                    {[
                      {
                        range: '-30 a -55 dBm',
                        label: t('scanner.interference.levels.critical.label'),
                        color: '#D32F2F',
                        desc: t(
                          'scanner.interference.levels.critical.shortDesc'
                        ),
                      },
                      {
                        range: '-56 a -70 dBm',
                        label: t('scanner.interference.levels.high.label'),
                        color: '#F97316',
                        desc: t('scanner.interference.levels.high.shortDesc'),
                      },
                      {
                        range: '-71 a -80 dBm',
                        label: t('scanner.interference.levels.medium.label'),
                        color: '#FACC15',
                        desc: t('scanner.interference.levels.medium.shortDesc'),
                      },
                      {
                        range: '-81 a -90 dBm',
                        label: t('scanner.interference.levels.low.label'),
                        color: '#00CFFF',
                        desc: t('scanner.interference.levels.low.shortDesc'),
                      },
                      {
                        range: '< -90 dBm',
                        label: t(
                          'scanner.interference.levels.negligible.label'
                        ),
                        color: '#4CAF50',
                        desc: t(
                          'scanner.interference.levels.negligible.shortDesc'
                        ),
                      },
                    ].map((item) => (
                      <InterferenceItemRow key={item.range}>
                        <InterferenceDot color={item.color} mt="4px" />
                        <Box>
                          <Typography
                            variant="caption"
                            fontWeight={700}
                            component="div"
                          >
                            <Box
                              component="span"
                              sx={{ color: theme.palette.white['06'] }}
                            >
                              {item.range} —{' '}
                            </Box>
                            <Box component="span" sx={{ color: item.color }}>
                              {item.label}
                            </Box>
                          </Typography>
                          <Typography
                            variant="caption"
                            color={theme.palette.white['06']}
                            display="block"
                            sx={{ opacity: 0.85 }}
                          >
                            {item.desc}
                          </Typography>
                        </Box>
                      </InterferenceItemRow>
                    ))}
                  </Stack>
                </Card>
              }
            >
              <Info
                size={14}
                color={theme.palette.primary.main}
                style={{ cursor: 'pointer', flexShrink: 0 }}
              />
            </DescriptionPopper>
          </TableColumnHeaderStack>
        ),
        width: '18%',
        render: (val) => {
          const power = Number(val);
          const info = getInterferenceInfo(power, t);
          const percent = Math.max(0, Math.min(100, (power + 100) * 2));

          const popperCard = (
            <Card maxWidth="320px" padding="14px 16px">
              <Stack direction="column" spacing="10px">
                {/* Header: Status and dBm */}
                <PopperHeaderStack>
                  <TableColumnHeaderStack>
                    <InterferenceDot color={info.color} size={10} />
                    <Typography
                      variant="body2"
                      fontWeight={700}
                      color={info.color}
                    >
                      {info.label}
                    </Typography>
                  </TableColumnHeaderStack>
                  <Typography
                    variant="caption"
                    fontWeight={700}
                    color={info.color}
                  >
                    {power} dBm
                  </Typography>
                </PopperHeaderStack>

                {/* Impact */}
                <Stack direction="column" spacing="2px">
                  <Typography
                    variant="caption"
                    fontWeight={600}
                    color={theme.palette.primary.main}
                  >
                    {t('scanner.interference.realImpact')}
                  </Typography>
                  <Typography
                    variant="caption"
                    color={theme.palette.white['06']}
                    sx={{ lineHeight: 1.4 }}
                  >
                    {info.impact}
                  </Typography>
                </Stack>

                {/* Recommended Action */}
                <Stack direction="column" spacing="2px">
                  <Typography
                    variant="caption"
                    fontWeight={600}
                    color={theme.palette.secondary['01']}
                  >
                    {t('scanner.interference.recommendedAction')}
                  </Typography>
                  <Typography
                    variant="caption"
                    color={theme.palette.white['06']}
                    sx={{ lineHeight: 1.4 }}
                  >
                    {info.action}
                  </Typography>
                </Stack>
              </Stack>
            </Card>
          );

          return (
            <DescriptionPopper
              placement="top"
              description={popperCard}
              width="100%"
              offset={[-20, 14]}
            >
              <SignalCellStack sx={{ cursor: 'pointer' }}>
                <SignalBarWrapper>
                  <CongestionBarFill
                    fillwidth={`${percent}%`}
                    fillcolor={info.color}
                  />
                </SignalBarWrapper>
                <SignalDbmTypography variant="body2" color={info.color}>
                  {power} dBm
                </SignalDbmTypography>
              </SignalCellStack>
            </DescriptionPopper>
          );
        },
      },
      // Security column
      {
        id: 'security',
        label: t('scanner.columns.security'),
        width: '16%',
        render: (val) => {
          const text = String(val || '—');
          return (
            <Tooltip title={text} arrow placement="top-start">
              <EllipsisTypography
                variant="body2"
                color={theme.palette.white['06']}
                noWrap
                component="span"
              >
                {text}
              </EllipsisTypography>
            </Tooltip>
          );
        },
      },
      // Standard column
      {
        id: 'standard',
        label: t('scanner.columns.standard'),
        width: '8%',
        render: (val) => (
          <StandardCellTypography variant="body2">
            {val || '—'}
          </StandardCellTypography>
        ),
      },
    ],
    [t, theme]
  );

  return (
    <TableComponent<WifiNetwork>
      data={sortedNetworks}
      columns={columns}
      page={page}
      setPage={setPage}
      rowsPerPage={8}
      size="small"
      tableLayout="fixed"
    />
  );
}
